// System status (docs/cockpit/spec.md, Setup): whether LAMP is working for the business now. The Cockpit and web chat
// (the Application API), background work (the worker), the AI models (the model gateway), each Channel and the last
// backup, each working, slow or down and since when, in plain words: never a host, a container or an error. State is
// said in words as well as shown, and never in safety amber, which is only for what the Owner must act on. LAMP's own
// Owners also read whether LAMP can issue its own tax invoices, and why a payment has none (#1011).
import { useCallback, useEffect, useState } from "react";
import { ApiError, type Api } from "../api.js";
import { formatMoment as moment } from "../i18n/format.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";
import type { LampInvoicesStatus, PartState, StatusView } from "./api.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

const TAG: Readonly<Record<PartState, string>> = {
  working: "tag tag-good",
  slow: "tag tag-plain",
  down: "tag tag-bad",
};

export function Status({ api }: { api: Api }) {
  const w = useWords();
  const { language } = useLanguage();
  const [status, setStatus] = useState<StatusView | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const check = useCallback(() => {
    setBusy(true);
    api.status
      .view()
      .then(
        (view) => {
          setStatus(view);
          setProblem(undefined);
        },
        (error: unknown) => setProblem(problemOf(error, w)),
      )
      .finally(() => setBusy(false));
  }, [api, w]);
  useEffect(check, [check]);

  if (!status) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.statusLoading}</p>;
  const rows = [
    { key: "api", name: w.statusPartName("api"), ...status.parts.api },
    { key: "worker", name: w.statusPartName("worker"), ...status.parts.worker },
    { key: "models", name: w.statusPartName("models"), ...status.parts.models },
    ...status.channels.map((c) => ({ key: `channel:${c.channel}`, name: w.statusChannelName(c.channel), ...c })),
  ];
  const states = [...rows, ...(status.backup ? [status.backup] : [])].map((r) => r.state);
  const headline = states.includes("down")
    ? w.statusSomeDown
    : states.includes("slow")
      ? w.statusSomeSlow
      : w.statusAllWorking;
  return (
    <>
      <p role="status">
        <strong>{headline}</strong>
      </p>
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{w.statusPart}</th>
              <th scope="col">{w.statusState}</th>
              <th scope="col">{w.statusSince}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <th scope="row">{r.name}</th>
                <td>
                  <span className={TAG[r.state]}>{w.statusStateName(r.state)}</span>
                </td>
                <td>{moment(r.since, language)}</td>
              </tr>
            ))}
            <tr>
              <th scope="row">{w.statusPartName("backup")}</th>
              {status.backup ? (
                <>
                  <td>
                    <span className={TAG[status.backup.state]}>{w.statusStateName(status.backup.state)}</span>
                  </td>
                  <td>
                    {status.backup.lastBackupAt
                      ? w.statusBackupAt(moment(status.backup.lastBackupAt, language))
                      : moment(status.backup.since, language)}
                  </td>
                </>
              ) : (
                <td colSpan={2}>{w.statusNoBackup}</td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      {status.lampInvoices ? <LampInvoices status={status.lampInvoices} /> : null}
      <p className="muted">
        {w.statusCheckedAt(moment(status.checkedAt, language))}{" "}
        <button type="button" className="btn btn-small" disabled={busy} onClick={check}>
          {w.statusCheckAgain}
        </button>
      </p>
    </>
  );
}

/** LAMP's own tax invoices (#1011), for LAMP's own Owners: whether LAMP can invoice, and each payment it has not. */
function LampInvoices({ status }: { status: LampInvoicesStatus }) {
  const w = useWords();
  return (
    <section aria-labelledby="status-lamp-invoices">
      <h2 id="status-lamp-invoices">{w.statusLampInvoices}</h2>
      {status.notReady ? (
        <p className="alert" role="alert">
          {w.statusLampInvoicesNotReady}
        </p>
      ) : (
        <p>{w.statusLampInvoicesReady}</p>
      )}
      {status.outstanding.length === 0 ? (
        <p>{w.statusLampInvoicesNoneWaiting}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{w.statusLampInvoicesTenant}</th>
                <th scope="col">{w.statusLampInvoicesPayment}</th>
                <th scope="col">{w.statusLampInvoicesWhy}</th>
              </tr>
            </thead>
            <tbody>
              {status.outstanding.map((o) => (
                <tr key={`${o.tenantId} ${o.payment}`}>
                  <td>{o.tenantId}</td>
                  <td>{o.payment}</td>
                  <td>{w.statusLampInvoiceReason(o.reason, o.said)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
